import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Rights } from 'src/app/models/enums/rights';
import { IOrganization } from 'src/app/models/interfaces/organization.interface';
import { Organization } from 'src/app/models/organization';
import { ParamsValues } from 'src/app/models/params-values';
import { DialogService } from 'src/app/services/dialog.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { ParamsValuesService } from 'src/app/services/params-values.service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { OrganizationDetailContext } from './organization-detail-context';
import { OrganizationDetailComponent } from './organization-detail/organization-detail.component';
import { OrganizationsListComponent } from './organizations-list/organizations-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-organizations',
    templateUrl: './organizations.component.html',
    styleUrls: ['./organizations.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, OrganizationsListComponent, DetailHeaderComponent, OrganizationDetailComponent],
})
export class OrganizationsComponent extends DefaultManagementComponent<IOrganization, Organization, string> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faAdd = faPlus;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Reference au composant OrganizationsListComponent.
     */
    @ViewChild(OrganizationsListComponent) organizationsListComponent: OrganizationsListComponent;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre organisations.
     */
    public defaultContext: OrganizationDetailContext = new OrganizationDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = false;
    public displayEditbuttom = false;

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param organizationsService : Service métier utilisé pour gérer les organisations.
     */
    constructor(
        protected override notificationService: NotificationService,
        private organizationsService: OrganizationsService,
        private paramsValuesService: ParamsValuesService,
        private memoryService: MemoryService,
        private router: Router,
        private spinner: NgxSpinnerService,
        private dialogService: DialogService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.OrganizationCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.OrganizationEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un client.
     * @param organization organization selectionné.
     */
    public onOrganizationSelected(organization: Organization) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadOrganization(organization.id).subscribe(
            (x) => {
                this.refresh(Organization.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'organisation." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param organization organization sélectionné.
     */
    public onOrganizationDisplayed(organization: Organization) {
        this.spinner.show('main-spinner');
        this.createContext();

        this.loadOrganization(organization.id).subscribe(
            (x) => {
                this.refresh(Organization.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'organisation." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param organization organization sélectionné.
     */
    public onOrganizationEdited(organization: Organization) {
        this.spinner.show('main-spinner');
        this.createContext();

        // Cas de la mise à jour.
        this.loadOrganization(organization.id).subscribe(
            (x) => {
                this.refresh(Organization.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer l'organisation." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Ajouter'.
     */
    public onOrganizationAdd() {
        this.spinner.show('main-spinner');
        this.organizationsService.ListChildren(this.memoryService.getSelectedOrganization().id, true).subscribe({
            next: (childrenOrganizations) => {
                this.spinner.hide('main-spinner');
                //this.memoryService.selectOrganization(this.memoryService.getConnectedUser().organization);

                if (childrenOrganizations.length > 1) {
                    let organizations = childrenOrganizations.filter((x) => !x.isAllowedToAccessHub4B);

                    this.dialogService.openOrganizationActivationDialog(organizations).subscribe((data) => {
                        if (!!data && !!data.selectedOrganization) {
                            this.spinner.show('main-spinner');
                            this.organizationsService.activate(data.selectedOrganization.id).subscribe({
                                next: () => {
                                    this.spinner.hide('main-spinner');
                                    this.notificationService.displaySuccess(null, 'Organisation activée');
                                    this.itemsList.refresh();
                                },
                                error: (error: HttpError) => {
                                    this.notificationService.displayError('Erreur', "Impossible d'activer l'organisation." + error.message);
                                    this.spinner.hide('main-spinner');
                                },
                            });
                        }
                    });
                }
            },
            error: (error: HttpError) => {
                this.notificationService.displayError('Erreur', error.message);
                this.spinner.hide('main-spinner');
            },
        });
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param organization : Valeur du client retourné par le formulaire.
     */
    public onOrganizationUpdated(organization: Organization): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            console.error('Not implemented');
        } else {
            // Cas de l'activation.
            this.organizationsService.activate(organization.id).subscribe(
                (x) => {
                    this.refreshWithNotification(Organization.createFormCopy(organization), 'Organisation activée', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible d'activer l'organisation." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appelé lors de la mise à jour des paramètres.
     * @param paramsValues : Valeur du paramètre à mettre à jour.
     */
    public onParamsValuesUpdated(paramsValues: ParamsValues): void {
        this.spinner.show('main-spinner');
        if (paramsValues.id) {
            this.paramsValuesService.update(this.context.formContext.item.id, paramsValues.id, paramsValues).subscribe(
                (x) => {
                    (this.context as OrganizationDetailContext).paramsContext.paramsValues = x;
                    this.refreshWithNotification(this.context.formContext.item, "Paramètres de l'organisation mis à jour", FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour le paramétrage de l'organisation." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            this.paramsValuesService.createForOrganization(this.context.formContext.item.id, paramsValues).subscribe(
                (x) => {
                    (this.context as OrganizationDetailContext).paramsContext.paramsValues = x;
                    this.refreshWithNotification(this.context.formContext.item, "Paramètres de l'organisation créés", FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de créer le paramétrage de l'organisation." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param organization organization sélectionné.
     */
    public onOrganizationDeleted(organization: Organization) {
        this.spinner.show('main-spinner');
        // Suppression effective de l'organization.
        this.organizationsService.delete(organization.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Organisation supprimée');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible de supprimer l'organisation." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param organization organisation sélectionné.
     */
    public onOrganizationDesactivated(organization: Organization) {
        this.spinner.show('main-spinner');
        // Désactivation de l'organisation.
        this.organizationsService.desactivate(organization.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Organisation désactivée');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible de désactiver l'organisation." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    public onOrganizationExported() {
        this.createContext();
        this.itemsList.export('organisations.xlsx');
    }

    public onOrganizationHelped() {
        this.itemsList.displayHelpDialog('organizations');
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Activer l'archivage' dans le menu contextuel.
     * @param organisation organisation sélectionnée.
     */
    public onOrganizationArchivingActivated(organization: Organization) {
        this.spinner.show('main-spinner');
        // Activation de l'archivage de l'organisation.
        this.organizationsService.activateArchiving(organization.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, "L'archivage de l'organisation a été activé");
                this.organizationsListComponent.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError("L'activation de l'archivage de l'organisation a échouée.", error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Charge les données d'un organization.
     * @param organizationId : Id du client à charger.
     */
    public loadOrganization(organizationId: string): Observable<Organization> {
        return this.organizationsService.get(organizationId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param organization : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(organization: IOrganization /*, paramsValues: IParamsValues*/): OrganizationDetailContext {
        let context = this.createContext();
        context.formContext.item = organization;
        context.editedItemId = organization.id;

        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): OrganizationDetailContext {
        let context = new OrganizationDetailContext();
        return context;
    }
}
